import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FiX, FiImage, FiEdit2, FiMinus } from "react-icons/fi";
import "./AddProductCategoryModal.scss";

interface AddProductCategoryModalProps {
    show: boolean;
    onClose: () => void;
    handleCreateCategory?: (payload: {
        name: string;
    }) => void
    handleUpdateCategory?: (payload: {
        name: string;
        id: number;
    }) => void;
    id?: number;
    type?: "create" | "update"
    initValues?: { name: string, description: string, icon: string | null }
}

const validationSchema = Yup.object().shape({
    name: Yup.string().required("Category name is required"),
    description: Yup.string().max(250, "Maximum 250 characters"),
    icon: Yup.mixed().nullable(),
});

export const AddProductCategoryModal: React.FC<AddProductCategoryModalProps> = ({
    show,
    onClose,
    handleCreateCategory,
    type = "create",
    initValues,
    id,
    handleUpdateCategory
}) => {
    if (!show) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-container">
                {/* Header */}
                <div className="modal-header">
                    <h2>{type === "create" ? "Add new" : "Update"} category</h2>
                    <FiX className="close-icon" onClick={onClose} />
                </div>

                {/* Form */}
                <Formik
                    initialValues={initValues ?? {
                        name: "",
                        description: "",
                        icon: null,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        if (type === "create" && handleCreateCategory) {
                            handleCreateCategory({ name: values.name })
                        } else if (type === "update" && id && handleUpdateCategory) {
                            handleUpdateCategory({ id, name: values.name })
                        }
                        // onClose();
                    }}
                >
                    {({ setFieldValue, values, isSubmitting }) => (
                        <Form className="modal-form">
                            {/* Product name */}
                            <label>Product name</label>
                            <Field
                                name="name"
                                placeholder="Enter category name"
                                className="input-field"
                            />
                            <span className="helper-text-url">
                                https://www.mamimarkets.com/category/
                                {values.name.toLowerCase().replace(/\s+/g, "-") || ""}
                            </span>
                            <ErrorMessage
                                name="name"
                                component="div"
                                className="error-text"
                            />

                            {/* Description */}
                            <label>Description</label>
                            <Field
                                as="textarea"
                                name="description"
                                className="textarea-field"
                                rows={3}
                            />
                            <span className="helper-text">Maximum of 250 characters</span>
                            <ErrorMessage
                                name="description"
                                component="div"
                                className="error-text"
                            />

                            {/* Category icon */}
                            <label>Category Icon</label>
                            {values.icon ? (
                                <div className="icon-preview">
                                    <img
                                        src={
                                            typeof values.icon === "string"
                                                ? values.icon
                                                : URL.createObjectURL(values.icon)
                                        }
                                        alt="Category Icon"
                                        className="uploaded-icon"
                                    />
                                    <button
                                        type="button"
                                        className="edit-btn"
                                        onClick={() =>
                                            document.getElementById("icon-input")?.click()
                                        }
                                    >
                                        <FiEdit2 size={30} />
                                    </button>
                                    <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={() => setFieldValue("icon", null)}
                                    >
                                        <FiMinus size={16} />
                                    </button>
                                    <input
                                        id="icon-input"
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={(e) =>
                                            setFieldValue("icon", e.currentTarget.files?.[0] || null)
                                        }
                                    />
                                </div>
                            ) : (
                                <div className="upload-box">
                                    <FiImage size={24} strokeWidth={3} />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) =>
                                            setFieldValue("icon", e.currentTarget.files?.[0] || null)
                                        }
                                    />
                                </div>
                            )}

                            {/* Submit button */}
                            <button type="submit" className="submit-btn" disabled={isSubmitting}>
                                {type === "create" ? !isSubmitting ? "Add new" : "Adding new" : isSubmitting ? "Updating" : "Update"} category
                            </button>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};
